import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router';
import type { ReactNode } from 'react';

import { cn } from '@/utils/cn';

export interface Breadcrumb {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: Breadcrumb[];
  headerActions?: ReactNode;
  meta?: ReactNode;
  className?: string;
}

export const PageHeader = ({
  title,
  description,
  breadcrumbs,
  headerActions,
  meta,
  className,
}: PageHeaderProps) => (
  <header className={cn('mb-6 flex flex-col gap-3', className)}>
    {breadcrumbs && breadcrumbs.length > 0 ? (
      <nav>
        <ol className="flex flex-wrap items-center gap-1 text-caption text-fg-secondary">
          {breadcrumbs.map((crumb, index) => (
            <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">
              {index > 0 ? (
                <ChevronRight aria-hidden className="h-3 w-3 text-fg-tertiary" />
              ) : null}
              {crumb.to ? (
                <Link to={crumb.to} className="hover:text-fg hover:underline">
                  {crumb.label}
                </Link>
              ) : (
                <span aria-current="page" className="text-fg">
                  {crumb.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    ) : null}

    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-page-title font-semibold text-fg">{title}</h1>
        {description ? (
          <p className="mt-1 max-w-2xl text-body-sm text-fg-secondary">{description}</p>
        ) : null}
        {meta ? <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div> : null}
      </div>

      {headerActions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{headerActions}</div>
      ) : null}
    </div>
  </header>
);
